/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type { ButtonProps } from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { Expand, ExpandList } from '../../../../../../../common/ui/expand-list';
import { LinkedResults } from '../../../../../../../common/ui/linked-results';
import { processExcerpt } from '../../../../../../../common/utils/search-result-utils';
import { convertVideoDuration } from './convert-video-duration';
import { formatTitleWithDate } from './meeting-transcript-renderer';
import { messages } from './messages';
import { BaseVideoSearchResult } from './shared/base-video-search-result';
import { isChapter } from './shared/is-chapter';
import type { ExternalChapter, LinkedResult, VideoResultRendererProps } from './shared/types';
import { DropdownItems } from './shared/video-chapters';

type ZoomMetadataContentLink = {
	readonly url?: string | null;
};

type ZoomVideoEntity = NonNullable<VideoResultRendererProps['data']['entity']> & {
	readonly metadataContentLinks?: ReadonlyArray<ZoomMetadataContentLink | null> | null;
	readonly metadataContentUrl?: string | null;
};

const styles = cssMap({
	bodyArea: {
		display: 'flex',
		columnGap: token('space.150'),
	},
});

const chevronIconAfter =
	(isExpanded: boolean): ButtonProps['iconAfter'] =>
	(iconProps) =>
		isExpanded ? (
			<ChevronDownIcon {...iconProps} size="small" />
		) : (
			<ChevronRightIcon {...iconProps} size="small" />
		);

const ZoomVideoFooter = ({
	data,
	chapters,
	url,
	linkedResults,
	sharedLinks,
	chatUrl,
	thirdPartyConfigs,
	onResultClicked,
	onResultContextMenu,
	position,
	productConfigs,
}: {
	data: VideoResultRendererProps['data'];
	chapters: ExternalChapter[];
	url: string;
	linkedResults: LinkedResult[];
	sharedLinks: string[];
	chatUrl?: string | null;
	thirdPartyConfigs: VideoResultRendererProps['thirdPartyConfigs'];
	onResultClicked: VideoResultRendererProps['onResultClicked'];
	onResultContextMenu?: VideoResultRendererProps['onResultContextMenu'];
	position?: number;
	productConfigs?: VideoResultRendererProps['productConfigs'];
}): JSX.Element => {
	const { formatMessage, formatDate } = useIntl();

	const pastMeetingLinkedResults = linkedResults.map((linkedResult) => ({
		...linkedResult,
		title: linkedResult.title
			? formatTitleWithDate(linkedResult.title, linkedResult.lastModifiedDate, formatDate)
			: linkedResult.title,
		videoDurationLabel: convertVideoDuration(linkedResult.durationInSeconds, {
			readable: true,
			formatMessage,
		}),
	}));

	return (
		<ExpandList
			parentPosition={position}
			additionalFooterButton={
				chatUrl ? (
					<LinkButton iconAfter={LinkExternalIcon} href={chatUrl} target="_blank" rel="noreferrer">
						{formatMessage(messages.inMeetingChat)}
					</LinkButton>
				) : undefined
			}
		>
			<Expand
				icon={null}
				iconAfter={chevronIconAfter}
				attachmentCount={chapters.length}
				attachmentType="url"
				label={formatMessage(messages.dropdownMessage, {
					chapterCount: chapters.length,
				})}
			>
				<DropdownItems chapters={chapters} url={url} shouldLinkTimestamps={false} />
			</Expand>
			<Expand
				icon={null}
				iconAfter={chevronIconAfter}
				attachmentCount={pastMeetingLinkedResults.length}
				attachmentType="url"
				label={formatMessage(messages.pastMeetings)}
			>
				<LinkedResults
					productConfigs={productConfigs}
					url={data.url}
					title={data.title}
					linkedResults={pastMeetingLinkedResults}
					thirdPartyConfigs={thirdPartyConfigs}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					position={position}
					fallbackType="video"
					description=""
					icon={null}
				/>
			</Expand>
			<Expand
				icon={null}
				iconAfter={chevronIconAfter}
				attachmentCount={sharedLinks.length}
				attachmentType="url"
				label={formatMessage(messages.sharedLinks)}
			>
				<LinkedResults
					productConfigs={productConfigs}
					{...data}
					links={sharedLinks}
					linkedResults={[]}
					url={url}
					thirdPartyConfigs={thirdPartyConfigs}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					position={position}
					fallbackType="video"
					icon={null}
				/>
			</Expand>
		</ExpandList>
	);
};

export const ZoomVideoRenderer = (props: VideoResultRendererProps): JSX.Element => {
	const {
		data,
		formatMessage,
		linkedResults,
		onResultClicked,
		onResultContextMenu,
		position,
		thirdPartyConfigs,
		productConfigs,
	} = props;
	const { url, excerpt } = data;
	const entity = data.entity as ZoomVideoEntity | null | undefined;
	const { durationInSeconds, chapters, metadataContentLinks, metadataContentUrl } = entity || {};

	const videoDuration = convertVideoDuration(durationInSeconds, { readable: true, formatMessage });
	const validChapters = (chapters ?? []).filter(isChapter) as ExternalChapter[];
	const sharedLinks = (metadataContentLinks ?? [])
		.map((metadataContentLink) => metadataContentLink?.url)
		.filter((metadataContentLink): metadataContentLink is string => Boolean(metadataContentLink));
	const zoomLinkedResults = linkedResults ?? [];
	const hasFooter =
		validChapters.length > 0 ||
		zoomLinkedResults.length > 0 ||
		sharedLinks.length > 0 ||
		Boolean(metadataContentUrl);
	const hasRelatedLinks = zoomLinkedResults.length > 0 || sharedLinks.length > 0;
	const footer = hasFooter ? (
		<ZoomVideoFooter
			data={data}
			chapters={validChapters}
			url={url}
			linkedResults={zoomLinkedResults}
			sharedLinks={sharedLinks}
			chatUrl={metadataContentUrl}
			thirdPartyConfigs={thirdPartyConfigs}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			position={position}
			productConfigs={productConfigs}
		/>
	) : undefined;

	const body = (
		<Box testId="zoom-video-body" xcss={styles.bodyArea}>
			<DescriptionBody description={processExcerpt(excerpt, true)} />
		</Box>
	);

	return (
		<BaseVideoSearchResult
			{...props}
			body={body}
			footer={footer}
			hasRelatedLinks={hasRelatedLinks}
			additionalMetadataParts={[['video-duration', videoDuration]]}
		/>
	);
};
